import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWaiterStore } from '../store/waiterStore';
import { WaiterOrderCard } from '../components/WaiterOrderCard';

interface ScannedOrder {
  orderId: string;
  date: string;
  client: string;
  phone: string;
  clientId: string;
  items: string[];
  total: number;
  bonusUsed: number;
  deliveryType: string;
  paymentType: string;
  status: string;
  tableNumber?: number;
}

const HISTORY_KEY = 'waiter-scanned-orders';

function loadHistory(): ScannedOrder[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function removeFromHistory(orderId: string): ScannedOrder[] {
  const history = loadHistory().filter(o => o.orderId !== orderId);
  localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
  return history;
}

function updateTableInHistory(orderId: string, table: number) {
  const history = loadHistory().map(o =>
    o.orderId === orderId ? { ...o, tableNumber: table } : o
  );
  localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
  return history;
}

export const WaiterDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { orders, waiter } = useWaiterStore();
  const [scannedHistory, setScannedHistory] = useState<ScannedOrder[]>(loadHistory);
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);

  // Обновляем историю при возврате со сканера
  useEffect(() => {
    const handleFocus = () => setScannedHistory(loadHistory());
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // Также обновляем при монтировании
  useEffect(() => {
    setScannedHistory(loadHistory());
  }, []);

  const handleRemove = (orderId: string) => {
    setScannedHistory(removeFromHistory(orderId));
    if (expandedOrderId === orderId) setExpandedOrderId(null);
  };

  const handleSetTable = (orderId: string, table: number) => {
    setScannedHistory(updateTableInHistory(orderId, table));
  };

  const activeOrders = orders.filter(o => o.status !== 'paid');
  const completedOrders = orders.filter(o => o.status === 'paid');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white">Заказы</h1>
          <p className="text-slate-400 text-sm">{waiter?.name}</p>
        </div>
        <button
          onClick={() => navigate('/waiter/scan')}
          className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium transition-colors flex items-center space-x-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
          </svg>
          <span>Сканировать QR</span>
        </button>
      </div>

      {/* ── Отсканированные заказы гостей (QR) ──────────────── */}
      {scannedHistory.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-amber-400 uppercase tracking-wider">
            Заказы гостей ({scannedHistory.length})
          </h2>
          {scannedHistory.map((order) => {
            const d = new Date(order.date);
            const isExpanded = expandedOrderId === order.orderId;
            return (
              <div
                key={order.orderId + order.date}
                className={`bg-slate-800/60 rounded-xl border transition-colors ${
                  isExpanded ? 'border-emerald-500/50' : 'border-slate-700/50'
                }`}
              >
                {/* Строка заказа */}
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <button
                      onClick={() => setExpandedOrderId(isExpanded ? null : order.orderId)}
                      className="flex-1 text-left"
                    >
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-white font-bold">#{order.orderId}</span>
                        {order.tableNumber ? (
                          <span className="px-2 py-0.5 bg-blue-500/20 border border-blue-500/30 text-blue-400 text-xs rounded-full font-medium">
                            Стол {order.tableNumber}
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 bg-red-500/20 border border-red-500/30 text-red-400 text-xs rounded-full font-medium">
                            Без стола
                          </span>
                        )}
                        <span className="text-emerald-400 font-semibold">{order.total} ₽</span>
                        <span className="text-slate-500 text-xs">{order.deliveryType}</span>
                      </div>
                      <p className="text-slate-400 text-sm">{order.client || order.phone || '—'}</p>
                      <p className="text-slate-500 text-xs">
                        {d.toLocaleDateString('ru-RU')} {d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </button>
                    <button
                      onClick={() => handleRemove(order.orderId)}
                      className="p-1 text-slate-600 hover:text-red-400 transition-colors ml-2 shrink-0"
                      title="Удалить"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Раскрытые детали */}
                {isExpanded && (
                  <div className="border-t border-slate-700/50 p-4 space-y-3">
                    {/* Стол */}
                    <select
                      value={order.tableNumber || ''}
                      onChange={(e) => handleSetTable(order.orderId, Number(e.target.value))}
                      className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 text-lg font-medium"
                    >
                      <option value="">Выберите стол</option>
                      {Array.from({ length: 20 }, (_, i) => i + 1).map(n => (
                        <option key={n} value={n}>Стол {n}</option>
                      ))}
                    </select>

                    {/* Получение / оплата */}
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Получение: <span className="text-white">{order.deliveryType}</span></span>
                      <span className="text-slate-400">Оплата: <span className="text-white">{order.paymentType}</span></span>
                    </div>

                    {/* Позиции */}
                    <div className="space-y-1">
                      {order.items.map((item, i) => (
                        <p key={i} className="text-white text-sm">{item}</p>
                      ))}
                    </div>

                    {/* Итого */}
                    <div className="border-t border-slate-700/50 pt-2">
                      {order.bonusUsed > 0 && (
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-slate-400">Бонусы</span>
                          <span className="text-emerald-400">−{order.bonusUsed} ₽</span>
                        </div>
                      )}
                      <div className="flex justify-between text-lg font-bold">
                        <span className="text-white">Итого</span>
                        <span className="text-emerald-400">{order.total} ₽</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Заказы официанта (через меню) ──────────────────── */}
      {activeOrders.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-emerald-400 uppercase tracking-wider">Активные ({activeOrders.length})</h2>
          {activeOrders.map(order => (
            <WaiterOrderCard key={order.id} order={order} onClick={() => navigate(`/waiter/order/${order.id}`)} />
          ))}
        </div>
      )}

      {activeOrders.length === 0 && scannedHistory.length === 0 && (
        <div className="text-center py-16">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-slate-800/50 border border-slate-700/50 mb-4">
            <svg className="w-10 h-10 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <p className="text-slate-400 text-lg">Нет заказов</p>
          <p className="text-slate-500 text-sm mt-1">Сканируйте QR-код заказа гостя</p>
        </div>
      )}

      {completedOrders.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Завершенные ({completedOrders.length})</h2>
          {completedOrders.slice(0, 5).map(order => (
            <WaiterOrderCard key={order.id} order={order} onClick={() => navigate(`/waiter/order/${order.id}`)} />
          ))}
        </div>
      )}
    </div>
  );
};
